import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import CustomerHeader from '../../components/CustomerHeader';
import CustomerFooter from '../../components/CustomerFooter';
import { customerApi, useCustomerAuth } from '../../lib/customerAuth';
import { formatInr } from '../../lib/pricing';
import { LoadingBlock, EmptyState } from '../../components/States';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import StatusPill from '../../components/StatusPill';
import { User, Package as PackageIcon, LogOut, MapPin, ArrowLeft, Truck, Clock } from 'lucide-react';

function AccountLayout({ children, title }) {
    const nav = useNavigate();
    const { customer, isAuthed, logout } = useCustomerAuth();
    useEffect(() => { if (!isAuthed) nav('/'); }, [isAuthed]);
    if (!customer) return null;
    return (
        <div className="bg-white min-h-screen">
            <CustomerHeader />
            <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                    <aside className="lg:col-span-3">
                        <Card className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="h-12 w-12 rounded-full flex items-center justify-center text-white font-bold" style={{ background: 'hsl(142 71% 45%)' }}>
                                    {(customer.name || 'U').split(' ').map(s => s[0]).slice(0, 2).join('').toUpperCase()}
                                </div>
                                <div>
                                    <div className="font-semibold">{customer.name}</div>
                                    <div className="text-xs text-neutral-500">{customer.mobile}</div>
                                    {customer.type === 'wholesale' && <div className="text-xs mt-0.5" style={{ color: 'hsl(142 71% 30%)' }}>Wholesale L{customer.level}</div>}
                                </div>
                            </div>
                        </Card>
                        <nav className="mt-3 space-y-1">
                            <Link to="/account" data-testid="acct-nav-profile" className="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-neutral-50 text-sm"><User className="h-4 w-4" /> Profile</Link>
                            <Link to="/account/orders" data-testid="acct-nav-orders" className="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-neutral-50 text-sm"><PackageIcon className="h-4 w-4" /> Orders</Link>
                            <button onClick={() => { logout(); nav('/'); }} className="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-neutral-50 text-sm text-red-600 w-full text-left"><LogOut className="h-4 w-4" /> Sign out</button>
                        </nav>
                    </aside>
                    <main className="lg:col-span-9">
                        <h1 className="text-2xl font-bold tracking-tight mb-4">{title}</h1>
                        {children}
                    </main>
                </div>
            </div>
            <CustomerFooter />
        </div>
    );
}

export function AccountProfile() {
    const { customer } = useCustomerAuth();
    return (
        <AccountLayout title="My Profile">
            {customer && (
                <Card className="p-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                        <div><div className="text-xs text-neutral-500">Name</div><div className="font-medium mt-0.5">{customer.name}</div></div>
                        <div><div className="text-xs text-neutral-500">Mobile</div><div className="font-medium mt-0.5">{customer.mobile}</div></div>
                        <div><div className="text-xs text-neutral-500">Email</div><div className="font-medium mt-0.5">{customer.email || '—'}</div></div>
                        <div><div className="text-xs text-neutral-500">Customer type</div><div className="font-medium mt-0.5 capitalize">{customer.type} {customer.type === 'wholesale' && `(Level ${customer.level})`}</div></div>
                    </div>
                    {customer.location && (
                        <div className="mt-4 pt-4 border-t">
                            <div className="text-xs text-neutral-500 flex items-center gap-1"><MapPin className="h-3 w-3" /> Saved Location</div>
                            <div className="font-medium mt-1 text-sm">
                                {customer.location.area}, {customer.location.district}, {customer.location.state} — {customer.location.pincode}
                            </div>
                        </div>
                    )}
                </Card>
            )}
        </AccountLayout>
    );
}

export function AccountOrders() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        (async () => {
            try { const r = await customerApi().get('/orders/mine/list'); setItems(r.data); }
            finally { setLoading(false); }
        })();
    }, []);
    return (
        <AccountLayout title="My Orders">
            {loading ? <LoadingBlock /> : items.length === 0 ? (
                <EmptyState icon={PackageIcon} title="No orders yet" description="Start shopping to see your orders here."
                    action={<Link to="/"><Button style={{ background: 'hsl(142 71% 45%)', color: 'white' }}>Shop now</Button></Link>} />
            ) : (
                <div className="space-y-3" data-testid="my-orders-list">
                    {items.map(o => (
                        <Link to={`/account/orders/${o.id}`} key={o.id}>
                            <Card className="p-4 hover:shadow-md transition-shadow" data-testid={`my-order-${o.order_number}`}>
                                <div className="flex items-center justify-between gap-3">
                                    <div>
                                        <div className="font-mono text-xs text-neutral-500">{o.order_number}</div>
                                        <div className="font-medium mt-0.5">{o.items?.length} item(s) · {formatInr(o.totals?.grand_total)}</div>
                                        <div className="text-xs text-neutral-500 mt-1">{new Date(o.created_at).toLocaleString()}</div>
                                    </div>
                                    <div className="flex flex-col items-end gap-1">
                                        <StatusPill status={o.status} />
                                        <StatusPill status={o.payment_status} />
                                    </div>
                                </div>
                            </Card>
                        </Link>
                    ))}
                </div>
            )}
        </AccountLayout>
    );
}

export function AccountOrderDetail() {
    const { id } = useParams();
    const nav = useNavigate();
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        (async () => {
            try { const r = await customerApi().get(`/orders/mine/${id}`); setOrder(r.data); }
            finally { setLoading(false); }
        })();
    }, [id]);
    return (
        <AccountLayout title="Order Details">
            <Button variant="ghost" size="sm" onClick={() => nav('/account/orders')} className="mb-3"><ArrowLeft className="h-4 w-4 mr-1" /> Back to orders</Button>
            {loading || !order ? <LoadingBlock /> : (
                <div className="space-y-4">
                    <Card className="p-5">
                        <div className="flex items-center justify-between flex-wrap gap-2">
                            <div>
                                <div className="font-mono text-xs text-neutral-500">{order.order_number}</div>
                                <div className="text-lg font-semibold mt-0.5">Total: {formatInr(order.totals?.grand_total)}</div>
                            </div>
                            <div className="flex gap-2">
                                <StatusPill status={order.status} />
                                <StatusPill status={order.payment_status} />
                            </div>
                        </div>
                        {order.tracking?.awb && (
                            <div className="mt-3 pt-3 border-t text-sm flex items-center gap-2">
                                <Truck className="h-4 w-4 text-blue-700" />
                                <span>Tracking: <span className="font-mono">{order.tracking.carrier} — {order.tracking.awb}</span></span>
                                {order.tracking.url && <a href={order.tracking.url} target="_blank" rel="noreferrer" className="text-blue-700 underline text-xs">Track</a>}
                            </div>
                        )}
                    </Card>
                    <Card className="p-5">
                        <div className="text-sm font-semibold mb-3 flex items-center gap-2"><Clock className="h-4 w-4" />Timeline</div>
                        <div className="space-y-3" data-testid="my-order-timeline">
                            {[...(order.timeline || [])].reverse().map((t, idx) => (
                                <div key={t.id} className="flex gap-3">
                                    <div className="flex flex-col items-center">
                                        <div className="h-2 w-2 rounded-full bg-green-500 mt-1.5" />
                                        {idx < order.timeline.length - 1 && <div className="w-px flex-1 bg-neutral-200 mt-1" />}
                                    </div>
                                    <div className="flex-1 pb-3">
                                        <div className="text-sm capitalize">{(t.to_status || '').replace(/_/g, ' ')}</div>
                                        {t.notes && <div className="text-xs text-neutral-600 mt-0.5">{t.notes}</div>}
                                        <div className="text-xs text-neutral-500 mt-0.5">{new Date(t.at).toLocaleString()}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </Card>
                    <Card className="p-5">
                        <div className="text-sm font-semibold mb-3">Items</div>
                        {order.items?.map(it => (
                            <div key={it.product_id} className="flex items-center gap-3 py-2 border-b last:border-0">
                                <div className="h-12 w-12 rounded bg-neutral-100 overflow-hidden shrink-0">
                                    {it.image && <img src={it.image} alt={it.name} className="h-full w-full object-cover" onError={(e) => e.currentTarget.style.display = 'none'} />}
                                </div>
                                <div className="flex-1"><div className="font-medium">{it.name}</div><div className="text-xs text-neutral-500 font-mono">{it.sku}</div></div>
                                <div className="text-sm text-neutral-600">{it.qty} × {formatInr(it.unit_price)}</div>
                                <div className="font-medium tabular-nums w-20 text-right">{formatInr(it.line_total)}</div>
                            </div>
                        ))}
                        <div className="mt-4 pt-4 border-t space-y-1 text-sm">
                            <div className="flex justify-between"><span>Subtotal</span><span className="tabular-nums">{formatInr(order.totals?.subtotal)}</span></div>
                            <div className="flex justify-between"><span>Shipping</span><span className="tabular-nums">{formatInr(order.totals?.shipping)}</span></div>
                            <div className="flex justify-between font-bold pt-2 border-t"><span>Total</span><span className="tabular-nums">{formatInr(order.totals?.grand_total)}</span></div>
                        </div>
                    </Card>
                </div>
            )}
        </AccountLayout>
    );
}
